import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../lib/utils';

const TYPE_COLORS = {
  mailing: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  billing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  shipping: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  other: 'bg-muted text-muted-foreground',
};

function formatAddress(a) {
  if (!a) return '';
  const csz = [a.city, a.state, a.zip_code].filter(Boolean).join(' ').trim();
  return [a.line1, a.line2, csz].filter(Boolean).join(', ');
}

/**
 * AddressPicker — shows a customer/vendor's saved addresses as cards and lets
 * the user pick which one to use (e.g., "Bill To" or "Ship To").
 *
 * Props:
 *  - addresses: array of AddressEntry
 *  - value: address_id (the currently selected one)
 *  - onChange: (address_id) => void
 *  - label: "Bill To" | "Ship To" | ...
 *  - preferType: 'billing' | 'shipping' | 'mailing' — used to highlight the
 *    matching type as a smart default suggestion.
 *  - testId: optional test id prefix
 */
export function AddressPicker({ addresses = [], value, onChange, label, preferType, testId = 'address-picker' }) {
  const [expanded, setExpanded] = useState(false);

  if (!addresses || addresses.length === 0) {
    return (
      <div className="rounded-xl border bg-muted/30 p-4 text-center text-xs text-muted-foreground" data-testid={`${testId}-empty`}>
        <MapPin className="w-5 h-5 mx-auto mb-1 opacity-50" />
        This contact has no saved addresses yet. <br />
        Add one on the contact's edit page.
      </div>
    );
  }

  // Determine effective selection: explicit value > preferred type > primary > first
  const findByType = addresses.find(a => a.type === preferType);
  const primary = addresses.find(a => a.is_primary);
  const effectiveId = value
    || findByType?.address_id
    || primary?.address_id
    || addresses[0]?.address_id;

  const selected = addresses.find(a => a.address_id === effectiveId) || addresses[0];

  // Collapsed view: show the selected card + "Change" toggle if there are more
  const otherCount = addresses.length - 1;

  return (
    <div className="space-y-2" data-testid={testId}>
      {label && (
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" /> {label}
        </div>
      )}

      {/* Selected (always visible) */}
      <Card
        className={cn(
          'border-2 transition-colors cursor-pointer',
          'border-primary/50 bg-primary/5'
        )}
        onClick={() => addresses.length > 1 && setExpanded(e => !e)}
        data-testid={`${testId}-selected`}
      >
        <CardContent className="p-3 flex items-start gap-3">
          <div className="mt-0.5 w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <Check className="w-3.5 h-3.5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <Badge variant="secondary" className={cn('text-[10px] capitalize', TYPE_COLORS[selected.type])}>
                {selected.type}
              </Badge>
              {selected.is_primary && <Badge className="text-[10px] bg-primary text-primary-foreground">Primary</Badge>}
              {selected.label && <span className="text-xs text-muted-foreground">— {selected.label}</span>}
            </div>
            <p className="text-sm">{formatAddress(selected) || <span className="italic text-muted-foreground">(empty address)</span>}</p>
          </div>
          {addresses.length > 1 && (
            <button
              type="button"
              className="text-xs text-primary hover:underline whitespace-nowrap flex items-center gap-1"
              onClick={(e) => { e.stopPropagation(); setExpanded(x => !x); }}
              data-testid={`${testId}-change-btn`}
            >
              {expanded ? <>Done <ChevronUp className="w-3 h-3" /></> : <>Change ({otherCount} more) <ChevronDown className="w-3 h-3" /></>}
            </button>
          )}
        </CardContent>
      </Card>

      {/* Expanded: show all OTHER addresses as one-click picks */}
      {expanded && addresses.length > 1 && (
        <div className="space-y-2 pl-4 border-l-2 border-primary/20" data-testid={`${testId}-options`}>
          {addresses
            .filter(a => a.address_id !== selected.address_id)
            .map((a) => (
              <Card
                key={a.address_id}
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => { onChange(a.address_id); setExpanded(false); }}
                data-testid={`${testId}-option-${a.address_id}`}
              >
                <CardContent className="p-3 flex items-start gap-3">
                  <div className="mt-0.5 w-6 h-6 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <Badge variant="secondary" className={cn('text-[10px] capitalize', TYPE_COLORS[a.type])}>
                        {a.type}
                      </Badge>
                      {a.is_primary && <Badge className="text-[10px] bg-primary text-primary-foreground">Primary</Badge>}
                      {a.label && <span className="text-xs text-muted-foreground">— {a.label}</span>}
                    </div>
                    <p className="text-sm text-muted-foreground">{formatAddress(a) || <span className="italic">(empty)</span>}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
}

export default AddressPicker;
